<template>
  <div class="filter-query-form-buttons">
    <slot name="prepend" />
    <el-button
      type="primary"
      :loading="queryButtonLoading"
      @click="$emit('query', $event)"
    >
      {{ queryButtonText }}
    </el-button>
    <el-button :loading="resetButtonLoading" @click="$emit('reset', $event)">
      {{ resetButtonText }}
    </el-button>
    <slot name="append" />
    <!-- 通过确认回调等待重置结果，避免 change 事件触发后弹窗提前关闭。 -->
    <QueryFieldSettings
      v-if="queryFieldSettingsForm"
      sortable
      :fields="queryFieldSettingsForm.$_queryFieldSettingsFields"
      :hidden-fields="queryFieldSettingsForm.queryFieldSettingsHidden"
      :order="queryFieldSettingsForm.queryFieldSettingsOrder"
      :before-confirm="changeQueryFieldSettings"
    />
    <slot />
  </div>
</template>

<script>
import QueryFieldSettings from '@/components/QueryFieldSettings'

export default {
  name: 'FilterQueryFormButtons',
  components: { QueryFieldSettings },
  inject: {
    queryFieldSettingsContainer: { default: null }
  },
  props: {
    queryButtonText: {
      type: String,
      default: 'query' // 为了凸显按钮权限文案与本地文案的区别，默认值使用 'query'
    },
    resetButtonText: {
      type: String,
      default: '重置'
    },
    queryButtonLoading: {
      type: Boolean,
      default: false
    },
    resetButtonLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    queryFieldSettingsForm() {
      return this.queryFieldSettingsContainer?.queryFieldSettingsForm
    }
  },
  methods: {
    /**
     * 完成通知临时放在表单配置中，公共 CRUD 自动反馈，页面无需透传 event。
     * 完全自行实现重置的页面仍可通过 event.onQuerySettingsReset 通知结果；
     * 没有成功通知时保留原配置，不能仅凭重置函数返回就认定筛选值已清空。
     */
    async changeQueryFieldSettings(hiddenFields, order) {
      const form = this.queryFieldSettingsForm
      // 只有显隐变化需要重置；纯排序保留当前输入且不触发查询。
      if (form.$_queryFieldSettingsVisibilityChanged(hiddenFields)) {
        const reset = this.$listeners.reset
        if (typeof reset !== 'function') {
          this.$message.error('请先配置页面的重置方法')
          return false
        }
        // 使用表单配置承接通知，不写入 data/listQuery，避免进入业务请求或筛选方案。
        const resetForm = form.value
        const accepted = await new Promise((resolve) => {
          let reported = false
          const onQuerySettingsReset = (success) => {
            reported = true
            resolve(success === true)
          }
          // onReset() 等旧包装方法即使不传参数，CRUD 也能从同一表单取到完成通知。
          this.$set(
            resetForm,
            '$_queryFieldSettingsResetNotify',
            onQuerySettingsReset
          )
          try {
            // 直接调用监听器才能检测未反馈的返回路径；$emit 本身不返回执行结果。
            Promise.resolve(reset({ onQuerySettingsReset })).then(
              () => {
                if (!reported) {
                  this.$message.error('页面未确认筛选条件已重置，设置尚未应用')
                  resolve(false)
                }
              },
              () => {
                if (!reported) {
                  this.$message.error('重置未完成，筛选项设置尚未应用')
                }
                resolve(false)
              }
            )
          } catch (error) {
            this.$message.error('重置未完成，筛选项设置尚未应用')
            resolve(false)
          }
        }).finally(() => {
          // 一次配置只绑定一次通知，避免普通重置或后续配置误用已结束的回调。
          this.$delete(resetForm, '$_queryFieldSettingsResetNotify')
        })
        if (!accepted) return false
      }
      // 收到重置成功通知后才更新显隐及缓存；被拒绝时保留原配置和弹窗草稿。
      form.$_queryFieldSettingsChange(hiddenFields, order)
      return true
    }
  }
}
</script>

<style lang="scss" scoped></style>
